import React from 'react';
import { Network } from 'lucide-react';
import MetricGroup, { MetricField } from '../MetricGroup';
import type { StreamStats } from '../../../types';
import { getLevel, ThresholdLevel } from '../../../utils/thresholds';

interface Props {
  stats: StreamStats;
  serverStats?: import('../../../types').ServerStats | null;
}

const NetworkFeedback: React.FC<Props> = ({ stats, serverStats }) => {
  const lossLevel: ThresholdLevel = getLevel('packet_loss_pct', stats.packetLoss);
  const rttLevel: ThresholdLevel = getLevel('rtt_ms', stats.rtt);
  const jitterLevel: ThresholdLevel = getLevel('jitter_buffer_ms', stats.latencyBreakdown.jitterBuffer);
  const freezeLevel: ThresholdLevel = getLevel('freeze_count', stats.freezeCount);
  const totalLatLevel: ThresholdLevel = getLevel('total_latency_ms', stats.latencyBreakdown.total);

  const isP2p = stats.transportType === 'p2p';
  const isRelay = stats.transportType === 'relay';

  const fields: MetricField[] = [
    { label: 'RTT', value: stats.rtt, unit: 'ms', level: rttLevel },
    { label: '丢包率', value: stats.packetLoss.toFixed(2), unit: '%', level: lossLevel },
    { label: '抖动缓冲', value: stats.latencyBreakdown.jitterBuffer.toFixed(1), unit: 'ms', level: jitterLevel },
    { label: '冻结次数', value: stats.freezeCount, unit: '次', level: freezeLevel,
      tooltip: `累计冻结时长 ${stats.totalFreezesDuration.toFixed(2)}s` },
    { label: 'NACK 总数', value: stats.nackCount, unit: '次',
      tooltip: '丢包重传请求数（NACK ≈ 丢包 × 包总数）' },
    { label: 'PLI 总数', value: stats.pliCount, unit: '次',
      tooltip: 'Picture Loss Indicator — 客户端请求关键帧次数' },
    { label: '连接类型', value: isP2p ? 'P2P' : isRelay ? 'TURN 中转' : '未知',
      level: isP2p ? 'normal' : 'warn' },
  ];

  if (stats.relayServer) {
    fields.push({ label: '中转服务器', value: stats.relayServer, tooltip: '浏览器侧 TURN 主机' });
  }

  // Stage 3A + Stage 3 时钟修正后: 细粒度 lag 分段
  // 时钟修正后 webrtc::Clock 精度 1ms（基于 timeGetTime），低于 1ms 的延迟显示为 0
  const sv = serverStats;
  if (sv && sv.broadcast_to_sink_lag_us_p50 !== undefined) {
    const bcastUs = sv.broadcast_to_sink_lag_us_p50 || 0;
    const vseUs = sv.capture_to_vse_call_lag_us_p50 || 0;
    const encodeUs = sv.capture_to_encode_lag_us_p50 || 0;

    // 微秒级显示（broadcast 同步调用通常 < 100us）
    fields.push({
      label: '分发耗时 (b)',
      value: bcastUs < 1000 ? bcastUs : (bcastUs / 1000).toFixed(2),
      unit: bcastUs < 1000 ? 'μs' : 'ms',
      tooltip: 'VideoBroadcaster 同步调用 N 个 sink->OnFrame 耗时（应 < 100μs）',
    });

    // VSE 调度延迟（vse_call - broadcast）—— 用微秒避免精度损失
    const vseSchedUs = Math.max(0, vseUs - bcastUs);
    fields.push({
      label: 'VSE 调度 (b→v)',
      value: vseSchedUs < 1000 ? `≤${vseSchedUs}` : (vseSchedUs / 1000).toFixed(1),
      unit: vseSchedUs < 1000 ? 'μs' : 'ms',
      tooltip: 'webrtc VSE encoder_queue 调度延迟 ≈ vse_call_lag - broadcast_lag。'
        + 'webrtc::Clock 精度 1ms，<1ms 显示为 ≤Xμs（实际可能更小）',
    });

    // wrapper 内（encode - vse_call）
    const wrapperUs = Math.max(0, encodeUs - vseUs);
    fields.push({
      label: 'wrapper 内 (v→e)',
      value: wrapperUs < 1000 ? `≤${wrapperUs}` : (wrapperUs / 1000).toFixed(2),
      unit: wrapperUs < 1000 ? 'μs' : 'ms',
      tooltip: 'NvencVideoEncoder/SoftwareVideoEncoder 内部检查耗时（订阅/初始化）',
    });
  }

  // 卡片级别
  const allLevels: ThresholdLevel[] = [lossLevel, rttLevel, jitterLevel, freezeLevel, totalLatLevel];
  const cardLevel: ThresholdLevel = allLevels.includes('crit')
    ? 'crit'
    : allLevels.includes('warn')
    ? 'warn'
    : 'normal';

  return (
    <MetricGroup
      title="网络反馈"
      icon={<Network size={14} />}
      level={cardLevel}
      primary={{
        label: '总延迟',
        value: stats.latencyBreakdown.total.toFixed(1),
        unit: 'ms',
        level: totalLatLevel,
      }}
      fields={fields}
    />
  );
};

export default NetworkFeedback;
